/**
 * @fileoverview Public IT handoff service for one-time SSO credential submission.
 * @module system-web/services/ssoHandoffService
 *
 * Responsibilities:
 * - Load handoff status and submit the Department IT contact's configuration.
 * - Keep opaque link tokens and credential payloads inside the service boundary.
 *
 * Dependencies:
 * - apiFetch for the public system-api handoff endpoints.
 *
 * Architecture:
 * - Public handoff operations are inherently online and server-owned.
 *
 * Notes:
 * - Tokens are encoded for path transport and never logged or persisted locally.
 *
 * @security Route tokens and client secrets are never logged, persisted, or sent
 *           to telemetry.
 * @localFirst The public handoff is inherently online; no staff tenant data is
 *             read outside the API-owned handoff transaction.
 */
import { apiFetch } from '../apiClient';

export interface SsoHandoffStatus {
  tenantName?: string;
  departmentName?: string;
  expiresAt?: string;
  setupStatus?: string;
  consumed?: boolean;
  expired?: boolean;
  packet?: { referenceId?: string; version?: number; status?: string };
  html?: string;
}

export function getSsoHandoff(token: string): Promise<SsoHandoffStatus> {
  return apiFetch(`/api/auth/sso-handoff/${encodeURIComponent(token)}`);
}

export function submitSsoHandoff(token: string, credentials: {
  clientId: string;
  clientSecret: string;
  upstreamTenantId: string;
  onMicrosoftDomain: string;
  email: string;
  secretExpiresAt: string;
  verifiedEmailDomains: string[];
  itConfirmations: Record<string, boolean>;
}): Promise<{ ok: boolean; setupStatus?: string }> {
  return apiFetch(`/api/auth/sso-handoff/${encodeURIComponent(token)}/submit`, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
}
